'use client'

import { useEffect } from 'react'
import { clsx } from 'clsx'
import { PriceLoader } from '@/components/PriceLoader/PriceLoader'
import { Skeleton } from '@/components/Skeleton/Skeleton'
import { OfferPresenter } from '@/features/priceCalculator/PurchaseForm/OfferPresenter/OfferPresenter'
import { EmploymentType } from '@/services/graphql/generated'
import { usePriceCalculatorStep } from '../usePriceCalculatorStep'
import { usePriceIntent } from '../usePriceIntent'
import { useShopSessionCustomer } from '../useShopSessionCustomer'
import { InsuranceDataForm } from './InsuranceDataForm/InsuranceDataForm'
import { useConfirmPriceIntent } from './InsuranceDataForm/useConfirmPriceIntent'
import { useTryConfirmPriceIntentWithSuggestedData } from './InsuranceDataForm/useTryConfirmPriceIntentWithSuggestedData'
import { PRELOADED_PRICE_INTENT_QUERY_PARAM, usePreloadedPriceIntentId } from './usePreloadedPriceIntentId'
import { centered, loadingSkeleton, priceLoaderWrapper, viewOffersWrapper } from './PurchaseForm.css'

export function PurchaseForm() {
  useWarnOnPreloadedPriceIntentId()

  const { priceIntent } = usePriceIntent()
  const customer = useShopSessionCustomer()
  const confirmPriceIntent = useConfirmPriceIntent()

  const { step, setStep } = usePriceCalculatorStep()
  const tryConfirmPriceIntentWithSuggestedData = useTryConfirmPriceIntentWithSuggestedData()

  useEffect(() => {
    // 1. Check if price intent is ready, otherwise the default step is 'loadingForm'
    // 2. Only setStep viewOffers if the form is loading to enable priceIntent
    // updates without going to step viewOffers before priceIntent is confirmed
    if (!priceIntent || step !== 'loadingForm') return

    // Loaded price intent has offers so we can show them right away
    if (priceIntent.offers.length > 0) {
      setStep('viewOffers')
      return
    }

    // ProductPurchase arrival (SSN already captured, SSN section skipped): apply the SSN-lookup
    // suggested data and confirm if that makes it ready, otherwise fall through to the form.
    if (priceIntent.product.name === 'SE_PAYMENT_PROTECTION' && customer?.ssn) {
      setStep('calculatingPrice')
      tryConfirmPriceIntentWithSuggestedData(priceIntent, {
        // Updating employment type before confirming the price intent allows a default value
        // for a field that otherwise requires user input
        additionalData: { employmentType: EmploymentType.PermanentEmployedFulltime },
        fallback: () => setStep('fillForm'),
      })
      return
    }

    setStep('fillForm')
  }, [priceIntent, customer?.ssn, setStep, step, confirmPriceIntent, tryConfirmPriceIntentWithSuggestedData])

  switch (step) {
    case 'loadingForm':
      return <Skeleton className={clsx(centered, loadingSkeleton)} />
    case 'fillForm':
      return <InsuranceDataForm className={centered} />
    case 'calculatingPrice':
      return (
        <div className={priceLoaderWrapper}>
          <PriceLoader />
        </div>
      )
    case 'viewOffers': {
      // The PurchaseForm mounts and renders when clicking on the "add more insurance" button.
      // At that point, the step is 'viewOffers' and the priceIntent is empty.
      // The component renders once before the effect runs and sets the step to 'fillForm'.
      // We don't want to render the component in that case, so we return null.
      if (priceIntent?.offers.length === 0) {
        return null
      }

      return (
        <div className={viewOffersWrapper}>
          <InsuranceDataForm />
          <OfferPresenter />
        </div>
      )
    }

    default:
      throw new Error(`Unexpected step: ${step}`)
  }
}

const useWarnOnPreloadedPriceIntentId = () => {
  const preloadedPriceIntentId = usePreloadedPriceIntentId()
  useEffect(() => {
    if (preloadedPriceIntentId != null) {
      console.warn(
        `preloadedPriceIntentId is not supported on PriceCalculatorPage, please find and update the code that sets ${PRELOADED_PRICE_INTENT_QUERY_PARAM} query param`,
      )
    }
  }, [preloadedPriceIntentId])
}
